/**
 * @Relay no-runtime
 */
/** @graphql cc **/
import type { MouseEvent, KeyboardEvent } from 'react';
import React from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { differenceInDays } from 'date-fns';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import AutomationIcon from '@atlaskit/icon/core/automation';
import EditBulkIcon from '@atlaskit/icon/core/edit-bulk';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import { LinkItem, Section } from '@atlaskit/menu';
import Lozenge from '@atlaskit/lozenge';
import { Inline } from '@atlaskit/primitives/compiled';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';
import {
	SPACE_PERMISSIONS_SETTINGS,
	SPACE_SETTINGS_AUTOMATION,
	SPACE_BULK_TOOLS,
	SPACE_ANALYTICS,
	SPACE_MEMBERS,
} from '@confluence/named-routes/entry-points/routes';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys';
import { isGraphQLError } from '@confluence/network';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { ReverseTrialLozengeListItemExperiment } from '@confluence/trial-components/entry-points/ReverseTrialListItem';
import { useReverseTrialLozengeState } from '@confluence/trial-components/entry-points/useReverseTrialLozengeStore';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode';

import type { SpaceMoreActionsMenuSpaceToolsQuery as SpaceMoreActionsMenuSpaceToolsQuery$data } from './__types__/SpaceMoreActionsMenuSpaceToolsQuery.ts';

type SpaceMoreActionsMenuSpaceToolsProps = {
	isSpaceAdmin: boolean;
	canManageSomeUsers: boolean;
	edition: ConfluenceEdition | null;
	spaceKey: string;
	onMenuItemClicked: (
		selection: string,
	) => (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void;
};

export const SpaceMoreActionsMenuSpaceTools = ({
	isSpaceAdmin,
	canManageSomeUsers,
	edition,
	spaceKey,
	onMenuItemClicked,
}: SpaceMoreActionsMenuSpaceToolsProps) => {
	const intl = useIntl();
	const { isRbacEnabled } = useSpaceRoleMode();
	const {
		canRenderTreatment: isReverseTrialLozengeExperimentTreatment,
		loading: isReverseTrialLozengeExperimentLoading,
		error: isReverseTrialLozengeExperimentError,
	} = useReverseTrialLozengeState();

	const showSpaceTools = isSpaceAdmin && edition === ConfluenceEdition.PREMIUM;

	const showUsers = canManageSomeUsers && edition !== ConfluenceEdition.FREE;

	const { data: trialData, error: trialDetailsError } =
		useQuery<SpaceMoreActionsMenuSpaceToolsQuery$data>(
			graphql`
				query SpaceMoreActionsMenuSpaceToolsQuery {
					license {
						trialEndDate
					}
				}
			`,
			{
				skip: !showSpaceTools,
			},
		);

	if (isGraphQLError(trialDetailsError) && !isErrorMarkedAsHandled(trialDetailsError)) {
		markErrorAsHandled(trialDetailsError);
	}

	const isPremiumTrial = fg('tako-4040_connie_trial_days_left_standardisation')
		? (getTrialDaysLeftFromTrialEndDate(trialData?.license?.trialEndDate) ?? 0) > 0
		: getTrialDaysLeft(trialData?.license?.trialEndDate) > 0;
	const isReverseTrialLozengeAbTreatment =
		isReverseTrialLozengeExperimentTreatment &&
		!isReverseTrialLozengeExperimentLoading &&
		!isReverseTrialLozengeExperimentError;

	return (
		showUsers && (
			<Section title={intl.formatMessage(i18n.spaceTools)} hasSeparator>
				<LinkItem
					href={
						isRbacEnabled
							? SPACE_MEMBERS.toUrl({
									spaceKey,
								})
							: SPACE_PERMISSIONS_SETTINGS.toUrl({
									spaceKey,
									tab: SpacePermissionsTabKey.Users,
								})
					}
					iconAfter={
						<ReverseTrialLozengeListItemExperiment
							touchpointId="confluence_space_tools_users_menu_item"
							edition={ConfluenceEdition.STANDARD}
						/>
					}
					iconBefore={<PeopleGroupIcon label="" color="currentColor" />}
					onClick={onMenuItemClicked('spaceAddUsers')}
					UNSAFE_shouldDisableRouterLink
					data-testid="space-add-users-link"
				>
					<FormattedMessage {...i18n.addUsers} />
				</LinkItem>
				{showSpaceTools && (
					<>
						<LinkItem
							href={SPACE_SETTINGS_AUTOMATION.toUrl({ spaceKey })}
							iconAfter={
								<ReverseTrialLozengeListItemExperiment
									touchpointId="confluence_space_settings_opt_automation_menu_item"
									edition={ConfluenceEdition.PREMIUM}
								/>
							}
							iconBefore={<AutomationIcon label="" color="currentColor" />}
							onClick={onMenuItemClicked('spaceAutomation')}
							UNSAFE_shouldDisableRouterLink
						>
							<Inline space="space.150" alignBlock="center">
								<FormattedMessage {...i18n.automation} />
								{isPremiumTrial && !isReverseTrialLozengeAbTreatment && (
									<Lozenge appearance="new">
										<FormattedMessage {...i18n.premiumLozenge} />
									</Lozenge>
								)}
							</Inline>
						</LinkItem>
						<LinkItem
							href={SPACE_BULK_TOOLS.toUrl({ spaceKey })}
							iconAfter={
								<ReverseTrialLozengeListItemExperiment
									touchpointId="confluence_space_settings_opt_content_manager_menu_item"
									edition={ConfluenceEdition.PREMIUM}
								/>
							}
							iconBefore={<EditBulkIcon label="" color="currentColor" />}
							onClick={onMenuItemClicked('spaceContentManager')}
							UNSAFE_shouldDisableRouterLink
						>
							<Inline space="space.150" alignBlock="center">
								<FormattedMessage {...i18n.contentManager} />
								{isPremiumTrial && !isReverseTrialLozengeAbTreatment && (
									<Lozenge appearance="new">
										<FormattedMessage {...i18n.premiumLozenge} />
									</Lozenge>
								)}
							</Inline>
						</LinkItem>
						<LinkItem
							href={SPACE_ANALYTICS.toUrl({ spaceKey })}
							iconAfter={
								<ReverseTrialLozengeListItemExperiment
									touchpointId="confluence_space_settings_opt_analytics_menu_item"
									edition={ConfluenceEdition.PREMIUM}
								/>
							}
							iconBefore={<ChartTrendIcon label="" color="currentColor" />}
							onClick={onMenuItemClicked('spaceAnalytics')}
							UNSAFE_shouldDisableRouterLink
						>
							<Inline space="space.150" alignBlock="center">
								<FormattedMessage {...i18n.analytics} />
								{isPremiumTrial && !isReverseTrialLozengeAbTreatment && (
									<Lozenge appearance="new">
										<FormattedMessage {...i18n.premiumLozenge} />
									</Lozenge>
								)}
							</Inline>
						</LinkItem>
					</>
				)}
			</Section>
		)
	);
};

// This is copied from next/packages/premium-onboarding/src/useTrialDetails.tsx
/**
 * @deprecated to be removed, new implementations should use @atlassian/upgrades-and-purchases
 */
const getTrialDaysLeft = (trialEndDate: string | null | undefined) => {
	if (!trialEndDate) {
		return 0;
	}

	// +1 day to round up for fractional days
	return differenceInDays(new Date(trialEndDate), new Date()) + 1;
};

const i18n = defineMessages({
	spaceTools: {
		id: 'side-navigation.space-navigation.space-tools',
		defaultMessage: 'Space tools',
		description: 'Section header within the more actions menu for Space tools.',
	},
	addUsers: {
		id: 'side-navigation.space-navigation.users',
		defaultMessage: 'Users',
		description:
			'Menu item to navigate to the space permissions tab in space settings that allows you to add users to a space. Appears in the space more actions menu.',
	},
	automation: {
		id: 'side-navigation.space-navigation.automation',
		defaultMessage: 'Automation',
		description:
			'Menu item to navigate to the automation page in space settings. Appears in the space more actions menu.',
	},
	contentManager: {
		id: 'side-navigation.space-navigation.content-manager',
		defaultMessage: 'Content manager',
		description:
			'Menu item to navigate to the content manager page in space settings. Appears in the space more actions menu.',
	},
	analytics: {
		id: 'side-navigation.space-navigation.analytics',
		defaultMessage: 'Analytics',
		description:
			'Menu item to navigate to the analytics page. Appears in the space more actions menu.',
	},
	premiumLozenge: {
		id: 'side-navigation.space-navigation.premium-lozenge',
		defaultMessage: 'Premium',
		description:
			'Lozenge text for the premium edition of Confluence. Appears in the space more actions menu to label premium space tools.',
	},
});
